import { useEffect, useState } from 'react'
import { useIntlayer } from 'react-intlayer'

type AssetLibraryAssistantProps = {
  onClose: () => void
  isMobile?: boolean
}

export const AssetLibraryAssistant = ({
  onClose,
  isMobile = false,
}: AssetLibraryAssistantProps) => {
  const intl = useIntlayer('assetLibraryAssistant')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Small delay to trigger animation
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300) // Wait for animation to complete
  }

  return (
    <div
      className={`absolute ${
        isMobile ? 'inset-x-4 bottom-20' : 'bottom-4 right-4 left-4'
      } max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-6 transform transition-all duration-300 z-50 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-2 right-2 p-1 hover:bg-blue-100 rounded-full transition-colors"
        aria-label={(intl.close as any)?.key || 'Close'}
      >
        <svg
          className="w-5 h-5 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Assistant icon */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
          <svg
            className="w-6 h-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">{intl.title}</h3>
          <p className="text-blue-700 mb-4">{intl.message}</p>

          {/* Email section */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-gray-700 mb-2">{intl.emailPrompt}</p>
            <a
              href="mailto:info@nicelittlethings.be?subject=Asset Library Feedback"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              info@nicelittlethings.be
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
